import React, { useEffect, useState } from "react";
import { Button } from "devextreme-react/button";
import { CheckBox } from "devextreme-react";
import { useSelector } from "react-redux";
import { Column, DataGrid, DataGridTypes, Grouping, Sorting, StateStoring } from "devextreme-react/data-grid";

import {
  selectReviewProductionIndicesColumns,
  selectReviewProductionIndicesDataSource,
  setReviewProductionIndicesColumns,
  setReviewProductionIndicesDataSource,
} from "@/redux/productionSlice";
import { store } from "@/store/store";
import { Utilities } from "@/shared/utils/utilities";
import { setToastInfo } from "@/redux/shareSlice";
import { FiscalPeriod } from "@/shared/models/ProductionDeterminantListing";
import { Pages } from "@/shared/constants";
import { usePopupDisplay } from "@/hooks/usePopupDisplay";
import NextButton from "@/components/NextButton";
import { ReviewProductionIndicesApi } from "@/shared/api/ReviewProductionIndices.api";
import { customGroupOrder, generateColumns, generateDataSource } from "@/shared/business/ReviewProductionIndices";
import ReviewProductionIndicesEditForm from "./EditForm";
import DataGridCustomizeCell from "./DataGridCustomizeCell";
import "./style.css";

const ReviewProductionIndices: React.FC = () => {
  const { popupVisible, showPopUp, hidePopup } = usePopupDisplay();

  const reviewProductionIndicesColumns = useSelector(selectReviewProductionIndicesColumns);
  const reviewProductionIndicesDataSource = useSelector(selectReviewProductionIndicesDataSource);

  const [isIndicesUploadChecked, setIsIndicesUploadChecked] = useState(false);
  const [uploadFileData, setUploadFileData] = useState(null as any);
  const [uploadFileName, setUploadFileName] = useState("");

  const [formData, setFormData] = useState({
    fiscalPeriod: {},
    determinant: {},
  } as any);

  useEffect(() => {
    if (reviewProductionIndicesColumns.length > 0 || reviewProductionIndicesDataSource.length > 0) {
      return;
    }

    loadingData();
  }, []);

  const runUpdate = async () => {
    if (await ReviewProductionIndicesApi.autoUpdateIndices()) {
      store.dispatch(setToastInfo(`Update indices success.`));
      await loadingData();
    }
  };

  const loadingData = async () => {
    var data = await ReviewProductionIndicesApi.getProductionIndices();
    if (!data || !data.fiscalPeriods || !data.groups) {
      return;
    }

    store.dispatch(setReviewProductionIndicesColumns(generateColumns(data)));
    store.dispatch(setReviewProductionIndicesDataSource(generateDataSource(data)));
  };

  const handleViewUpdateLogs = async () => {
    var res = await ReviewProductionIndicesApi.getAutoUpdateLog();
    if (res) {
      var fileName = "AutoIndexUpdateLog.txt";
      Utilities.fileDownload(res.data, fileName, res.headers["content-type"]);
      store.dispatch(setToastInfo(`Logs were downloaded successfully as ${fileName}.`));
    }
  };

  const handleExportIndices = async () => {
    var fileName = await ReviewProductionIndicesApi.getProductionDeterminantsExcel();
    !Utilities.isNullOrEmptyString(fileName) &&
      store.dispatch(setToastInfo(`Indices were downloaded successfully as ${fileName}.`));
  };

  const handleFileChange = (e: any) => {
    setUploadFileName(e.target.value);

    const file = e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
    setUploadFileData(file);
  };

  const handleUpload = async () => {
    if (uploadFileData === null || uploadFileName === "") {
      return;
    }

    if (await ReviewProductionIndicesApi.uploadIndices(uploadFileData, isIndicesUploadChecked)) {
      store.dispatch(setToastInfo("Indices uploaded successfully"));
    }
  };

  const handleClear = () => {
    setUploadFileName("");
    setUploadFileData(null);
  };

  const handleEditDetail = (e: DataGridTypes.ColumnCellTemplateData, fiscalPeriod: FiscalPeriod) => {
    setFormData({
      determinant: (e.data as any).determinantObject,
      fiscalPeriod,
    });
    showPopUp();
  };

  const handleGetCurrentRate = async (e: DataGridTypes.ColumnCellTemplateData, fiscalPeriod: FiscalPeriod) => {
    if (await ReviewProductionIndicesApi.updateExchangeRateIndex(fiscalPeriod.fiscalYear, fiscalPeriod.fiscalMonth)) {
      await loadingData();
    }
  };

  const handleFormClose = async (reloadData: boolean) => {
    hidePopup();
    if (reloadData) {
      await loadingData();
    }
  };

  return (
    <>
      <div id="review-production-indices-dg">
        <DataGrid
          dataSource={reviewProductionIndicesDataSource}
          // columns={Utilities.dataClone(reviewProductionIndicesColumns)}
          paging={{ enabled: false }}
          showBorders={true}
          allowColumnResizing={true}
          repaintChangesOnly={false}
          hoverStateEnabled={true}
          wordWrapEnabled={false}
          columnAutoWidth={true}
          height={700}
          keyExpr="sortIndex"
          focusedRowEnabled={true}

        // sortingMode={"none"}
        >
          {/* <ColumnChooser enabled={false} /> */}
          <Sorting mode="none" />
          <Grouping autoExpandAll={true} allowCollapsing={false} />
          {/* <RemoteOperations sorting={true} /> */}
          <StateStoring enabled={false} />

          <Column
            dataField="CategoryName"
            groupIndex={0}
            allowSorting={false}
            sortingMethod={(v1, v2) => (customGroupOrder.indexOf(v1.CategoryName) > customGroupOrder.indexOf(v2.CategoryName) ? 1 : 0)}
            groupCellRender={(cellInfo) => <span className="text-end">{cellInfo.value}</span>}
          />

          {reviewProductionIndicesColumns.map((column) => (
            <Column
              key={column.dataField}
              dataField={column.dataField}
              caption={column.caption}
              groupIndex={column.groupIndex}
              fixed={column.fixed}
              fixedPosition={column.fixedPosition}
              cssClass={column.cssClass}
            >
              {column.columns &&
                column.columns.map((subColumn) => (
                  <Column
                    key={subColumn.dataField}
                    dataField={subColumn.dataField}
                    caption={subColumn.caption}
                    fixed={subColumn.fixed}
                    fixedPosition={subColumn.fixedPosition}
                    cssClass={subColumn.cssClass}
                    cellRender={subColumn.fixed ? undefined : (e) => DataGridCustomizeCell(e, subColumn.fiscalPeriod, handleEditDetail, handleGetCurrentRate)}
                  />
                ))}
            </Column>
          ))}
        </DataGrid>
      </div>

      <div className="mt-2">
        <Button icon="refresh" text="RUN UPDATE" type="default" onClick={runUpdate}></Button>
        <Button type="default" className="ms-3" onClick={handleViewUpdateLogs}>
          VIEW UPDATE LOGS
        </Button>
        <Button type="default" icon="export" text="EXPORT INDICES" className="ms-3" onClick={handleExportIndices} />
        <CheckBox className="ms-3" text="Indices Upload" value={isIndicesUploadChecked} onValueChange={(val) => setIsIndicesUploadChecked(val as boolean)} />
        <input
          className="ms-3"
          type="file"
          value={uploadFileName}
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={handleFileChange}
        />
        {/* <FileUploader id="file-uploader" selectButtonText="Choose File" labelText="" accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" uploadMode="useForm" /> */}
        <Button type="default" className="ms-3" disabled={uploadFileData === null} onClick={handleUpload}>
          UPLOAD
        </Button>
        <Button type="default" className="ms-3" onClick={handleClear}>
          CLEAR
        </Button>
        <NextButton nextPage={Pages.RunSurchargeIncrease} />
      </div>

      {popupVisible && (
        <ReviewProductionIndicesEditForm
          visible={popupVisible}
          closeForm={handleFormClose}
          fiscalPeriod={formData.fiscalPeriod}
          determinant={formData.determinant}
        ></ReviewProductionIndicesEditForm>
      )}
    </>
  );
};

export default ReviewProductionIndices;
